/**
 * useStrategicStream - Hook for consuming Strategic Blueprint SSE events
 *
 * Handles real-time streaming updates from the strategic analysis backend:
 * - workflow_start: Initial workflow metadata
 * - engagement_manager_start/complete: Issue tree architect phase
 * - workstream_start/complete: Per-workstream lifecycle
 * - workstream_agent_start/complete: Per-agent within workstream
 * - conflict_detected/resolved: Cross-workstream conflicts
 * - stress_test_start/complete: Hypothesis validation
 * - debate_start/complete: AI council debate
 * - debate_agent_argument: Individual debate contributions
 * - mediator_start/complete: Consensus building
 * - ghost_deck_generated: Slide deck ready
 * - done: Final deliverable
 * - error: Error occurred
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE_URL } from '@/lib/api';

// ============================================================================
// TYPES
// ============================================================================

export type StrategicPhase =
  | 'idle'
  | 'planning'
  | 'engagement_manager'
  | 'data_anchoring'
  | 'workstreams'
  | 'synthesis'
  | 'stress_test'
  | 'debate'
  | 'mediation'
  | 'aggregation'
  | 'concluded'
  | 'error';

export type StrategicIntensity = 'fast' | 'deep';

/** Source/citation from an agent */
export interface StrategicSource {
  url: string | null;
  title: string;
  type: 'web' | 'citation';
  citationNumber?: number;
}

/** Agent within a workstream */
export interface StrategicAgent {
  workstreamId: string;
  agentIndex: number;
  agentRole: string;
  model: string;
  status: 'idle' | 'running' | 'complete' | 'error';
  outputPreview?: string;
  fullOutput?: string;
  keyFindings?: string[];
  sources?: StrategicSource[];
  latencySeconds?: number;
  citationCount: number;
}

/** A workstream with its agents */
export interface StrategicWorkstream {
  id: string;
  name: string;
  keyQuestions: string[];
  agents: StrategicAgent[];
  status: 'pending' | 'running' | 'complete';
  synthesisPreview?: string;
  keyFindings: string[];
  totalCitations: number;
  latencySeconds?: number;
}

/** Conflict detected between workstreams */
export interface StrategicConflict {
  conflictId: string;
  workstreamA: string;
  workstreamB: string;
  findingA: string;
  findingB: string;
  severity: 'critical' | 'significant' | 'minor';
  description: string;
  resolution?: string;
  resolutionRationale?: string;
  resolved: boolean;
}

/** Stress test result for an assumption */
export interface StressTestResult {
  assumptionId: string;
  assumptionStatement: string;
  testResult: 'validated' | 'invalidated' | 'inconclusive';
  evidenceFor: string[];
  evidenceAgainst: string[];
  confidence: number;
  critique?: string;
}

/** Debate argument from AI council member */
export interface DebateArgument {
  debaterRole: string;
  debaterName: string;
  roundNumber: number;
  position: string;
  argument: string;
  evidenceCited: string[];
  counterPoints: string[];
  confidence: number;
  timestamp: number;
}

/** Mediator consensus */
export interface MediatorConsensus {
  consensusReached: boolean;
  winningPosition?: string;
  confidence: number;
  keyAgreements: string[];
  unresolvedTensions: string[];
  finalRecommendation?: string;
  rationale?: string;
}

/** Ghost deck slide */
export interface GhostDeckSlide {
  slideNumber: number;
  title: string;
  headlineInsight?: string;
  keyPoints: string[];
  supportingEvidence: string[];
  workstreamSource?: string;
  slideType: string;
}

/** Competitor data from FMP API */
export interface CompetitorData {
  ticker: string;
  companyName: string;
  sector?: string;
  industry?: string;
  price?: number;
  priceFormatted?: string;
  change?: number;
  changePercent?: number;
  changeFormatted?: string;
  marketCap?: number;
  marketCapFormatted?: string;
  peRatio?: number;
  peFormatted?: string;
  eps?: number;
  epsFormatted?: string;
  volume?: number;
  avgVolume?: number;
  dayHigh?: number;
  dayLow?: number;
  yearHigh?: number;
  yearLow?: number;
  '52WeekRange'?: string;
  employees?: number;
  website?: string;
  fetchedAt?: string;
}

/** Strategic kill sheet (final deliverable) */
export interface StrategicKillSheet {
  executiveSummary: string;
  recommendation: string;
  confidenceLevel: string;
  keyFindings: string[];
  risks: string[];
  opportunities: string[];
  nextSteps: string[];
}

/** Final strategic deliverable */
export interface StrategicDeliverable {
  killSheet?: StrategicKillSheet;
  ghostDeck?: GhostDeckSlide[];
  executiveSummary?: string;
  totalLatencySeconds?: number;
}

/** Full streaming state */
export interface StrategicStreamingState {
  phase: StrategicPhase;
  topic: string;
  intensity: StrategicIntensity;
  /** Effective intensity after tier adjustments (e.g., trial → quick) */
  effectiveIntensity?: StrategicIntensity;
  workstreamCount: number;
  totalAgents: number;
  estimatedSeconds: number;
  /** Estimated credit cost for this analysis */
  estimatedCredits?: number;
  workstreams: StrategicWorkstream[];
  conflicts: StrategicConflict[];
  stressTestResults: StressTestResult[];
  debateArguments: DebateArgument[];
  debateRound: number;
  debateRounds: number;
  mediatorConsensus?: MediatorConsensus;
  ghostDeck: GhostDeckSlide[];
  deliverable?: StrategicDeliverable;
  competitorData: CompetitorData[];
  error?: string;
  startedAt?: string;
  completedAt?: string;
  /** Actual credits consumed (from orchestrator done event) */
  totalCreditsUsed?: number;
  /** Set when backend rejects due to insufficient credits */
  insufficientCredits?: { required: number; available: number };
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: StrategicStreamingState = {
  phase: 'idle',
  topic: '',
  intensity: 'fast',
  workstreamCount: 0,
  totalAgents: 0,
  estimatedSeconds: 0,
  workstreams: [],
  conflicts: [],
  stressTestResults: [],
  debateArguments: [],
  debateRound: 0,
  debateRounds: 0,
  ghostDeck: [],
  competitorData: [],
};

// ============================================================================
// HOOK OPTIONS
// ============================================================================

interface UseStrategicStreamOptions {
  onComplete?: (deliverable: StrategicDeliverable) => void;
  onError?: (error: string) => void;
}

interface UseStrategicStreamReturn {
  state: StrategicStreamingState;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  startAnalysis: (
    topic: string,
    intensity: StrategicIntensity,
    options?: {
      enableStressTest?: boolean;
      enableDebate?: boolean;
      enableDataAnchor?: boolean;
    }
  ) => void;
  disconnect: () => void;
  reset: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useStrategicStream(
  options?: UseStrategicStreamOptions
): UseStrategicStreamReturn {
  const [state, setState] = useState<StrategicStreamingState>(initialState);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const onCompleteRef = useRef(options?.onComplete);
  const onErrorRef = useRef(options?.onError);
  onCompleteRef.current = options?.onComplete;
  onErrorRef.current = options?.onError;

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setState(initialState);
    setError(null);
  }, [disconnect]);

  const startAnalysis = useCallback(
    async (
      topic: string,
      intensity: StrategicIntensity,
      analysisOptions?: {
        enableStressTest?: boolean;
        enableDebate?: boolean;
        enableDataAnchor?: boolean;
      }
    ) => {
      // Reset state
      setState({
        ...initialState,
        topic,
        intensity,
        phase: 'planning',
      });
      setError(null);
      setIsConnecting(true);

      const url = `${API_BASE_URL}/api/v1/strategic/analyze/stream`;

      // Get auth token from localStorage
      const token = localStorage.getItem('access_token');

      // Create abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            topic,
            intensity,
            enable_stress_test: analysisOptions?.enableStressTest ?? true,
            enable_debate: analysisOptions?.enableDebate ?? true,
            enable_data_anchor: analysisOptions?.enableDataAnchor ?? true,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        setIsConnected(true);
        setIsConnecting(false);

        // Process SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              let eventType = '';
              let eventData = '';

              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                } else if (line === '' && eventType && eventData) {
                  handleSSEEvent(
                    eventType,
                    eventData,
                    setState,
                    disconnect,
                    onCompleteRef,
                    onErrorRef
                  );
                  eventType = '';
                  eventData = '';
                }
              }
            }
          } catch (e) {
            if ((e as Error).name !== 'AbortError') {
              console.error('Strategic stream reading error:', e);
              setError('Connection lost');
            }
          } finally {
            setIsConnected(false);
          }
        };

        processStream();
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        const errorMsg = e instanceof Error ? e.message : 'Failed to start analysis';
        setError(errorMsg);
        setIsConnecting(false);
        setState((prev) => ({ ...prev, phase: 'error', error: errorMsg }));
        onErrorRef.current?.(errorMsg);
      }
    },
    [disconnect]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    state,
    isConnected,
    isConnecting,
    error,
    startAnalysis,
    disconnect,
    reset,
  };
}


// ============================================================================
// SSE EVENT HANDLER
// ============================================================================

function handleSSEEvent(
  eventType: string,
  eventData: string,
  setState: React.Dispatch<React.SetStateAction<StrategicStreamingState>>,
  disconnect: () => void,
  onCompleteRef: React.MutableRefObject<
    ((deliverable: StrategicDeliverable) => void) | undefined
  >,
  onErrorRef: React.MutableRefObject<((error: string) => void) | undefined>
) {
  try {
    const data = JSON.parse(eventData);
    console.log('[Strategic SSE] Event:', eventType, data);

    switch (eventType) {
      case 'insufficient_credits':
        setState((prev) => ({
          ...prev,
          phase: 'error',
          error: `Insufficient credits: need ${data.required}, have ${data.available}`,
          insufficientCredits: { required: data.required, available: data.available },
        }));
        onErrorRef.current?.(`Insufficient credits: need ${data.required}, have ${data.available}`);
        disconnect();
        break;

      case 'workflow_start':
        setState((prev) => ({
          ...prev,
          phase: 'planning',
          topic: data.topic || prev.topic,
          workstreamCount: data.workstream_count || 0,
          totalAgents: data.total_agents || 0,
          estimatedSeconds: data.estimated_seconds || 0,
          estimatedCredits: data.estimated_credits,
          effectiveIntensity: data.effective_intensity as StrategicIntensity | undefined,
          startedAt: data.started_at,
        }));
        break;

      case 'engagement_manager_start':
        setState((prev) => ({ ...prev, phase: 'engagement_manager' }));
        break;

      case 'engagement_manager_complete':
        setState((prev) => ({
          ...prev,
          workstreamCount: data.workstream_count || prev.workstreamCount,
        }));
        break;

      case 'data_anchor_start':
        setState((prev) => ({ ...prev, phase: 'data_anchoring' }));
        break;

      case 'data_anchor_complete': {
        // Extract competitor data from the data_points field
        const dataPoints = data.data_points || {};
        const competitors: CompetitorData[] = dataPoints.competitors || [];
        setState((prev) => ({
          ...prev,
          competitorData: competitors,
        }));
        break;
      }

      case 'workstream_start': {
        const newWorkstream: StrategicWorkstream = {
          id: data.workstream_id,
          name: data.workstream_name,
          keyQuestions: data.key_questions || [],
          agents: [],
          status: 'running',
          keyFindings: [],
          totalCitations: 0,
        };
        setState((prev) => ({
          ...prev,
          phase: 'workstreams',
          workstreams: [...prev.workstreams, newWorkstream],
        }));
        break;
      }

      case 'workstream_agent_start': {
        const newAgent: StrategicAgent = {
          workstreamId: data.workstream_id,
          agentIndex: data.agent_index,
          agentRole: data.agent_role,
          model: data.model,
          status: 'running',
          citationCount: 0,
        };
        setState((prev) => ({
          ...prev,
          workstreams: prev.workstreams.map((ws) =>
            ws.id === data.workstream_id
              ? { ...ws, agents: [...ws.agents, newAgent] }
              : ws
          ),
        }));
        break;
      }

      case 'workstream_agent_complete': {
        // Map sources from backend format to frontend format
        const mappedSources: StrategicSource[] = (data.sources || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (s: any) => ({
            url: s.url,
            title: s.title || 'Source',
            type: s.type || 'web',
            citationNumber: s.citation_number,
          })
        );
        setState((prev) => ({
          ...prev,
          workstreams: prev.workstreams.map((ws) =>
            ws.id === data.workstream_id
              ? {
                  ...ws,
                  agents: ws.agents.map((a) =>
                    a.agentIndex === data.agent_index
                      ? {
                          ...a,
                          status: data.status === 'error' ? 'error' : 'complete',
                          outputPreview: data.output_preview,
                          fullOutput: data.full_output,
                          keyFindings: data.key_findings || [],
                          sources: mappedSources,
                          latencySeconds: data.latency_seconds,
                          citationCount: data.citation_count || mappedSources.length,
                        }
                      : a
                  ),
                }
              : ws
          ),
        }));
        break;
      }

      case 'workstream_synthesis':
        setState((prev) => ({
          ...prev,
          workstreams: prev.workstreams.map((ws) =>
            ws.id === data.workstream_id
              ? {
                  ...ws,
                  synthesisPreview: data.synthesis_preview,
                  keyFindings: data.key_findings || [],
                }
              : ws
          ),
        }));
        break;

      case 'workstream_complete':
        setState((prev) => ({
          ...prev,
          workstreams: prev.workstreams.map((ws) =>
            ws.id === data.workstream_id
              ? {
                  ...ws,
                  status: 'complete',
                  keyFindings: data.key_findings || ws.keyFindings,
                  totalCitations: data.total_citations || 0,
                  latencySeconds: data.latency_seconds,
                }
              : ws
          ),
        }));
        break;

      case 'synthesis_start':
        setState((prev) => ({ ...prev, phase: 'synthesis' }));
        break;

      case 'synthesis_complete':
        // Synthesis complete
        break;

      case 'conflict_detected': {
        const conflict: StrategicConflict = {
          conflictId: data.conflict_id,
          workstreamA: data.workstream_a,
          workstreamB: data.workstream_b,
          findingA: data.finding_a,
          findingB: data.finding_b,
          severity: data.severity,
          description: data.description,
          resolved: false,
        };
        setState((prev) => ({
          ...prev,
          conflicts: [...prev.conflicts, conflict],
        }));
        break;
      }

      case 'conflict_resolved':
        setState((prev) => ({
          ...prev,
          conflicts: prev.conflicts.map((c) =>
            c.conflictId === data.conflict_id
              ? {
                  ...c,
                  resolved: true,
                  resolution: data.resolution,
                  resolutionRationale: data.resolution_rationale,
                }
              : c
          ),
        }));
        break;

      case 'stress_test_start':
        setState((prev) => ({ ...prev, phase: 'stress_test' }));
        break;

      case 'stress_test_result':
      case 'hypothesis_tested': {
        // Handle both event names for compatibility
        const result: StressTestResult = {
          assumptionId: data.assumption_id || data.hypothesis_id,
          assumptionStatement: data.assumption_statement || data.hypothesis_text,
          testResult: data.test_result || (data.stress_score >= 0.6 ? 'validated' : 'invalidated'),
          evidenceFor: data.evidence_for || data.recommendations?.filter((_: string, i: number) => i < 2) || [],
          evidenceAgainst: data.evidence_against || data.recommendations?.filter((_: string, i: number) => i >= 2) || [],
          confidence: data.confidence || data.stress_score || 0.5,
          critique: data.critique,
        };
        setState((prev) => ({
          ...prev,
          stressTestResults: [...prev.stressTestResults, result],
        }));
        break;
      }

      case 'stress_test_complete':
        // Stress test complete
        break;

      case 'debate_start':
        setState((prev) => ({
          ...prev,
          phase: 'debate',
          debateRounds: data.debate_rounds || 1,
          debateRound: 1,
        }));
        break;

      // Handle both event names for backward compatibility
      case 'debate_argument':
      case 'debate_agent_argument': {
        const argument: DebateArgument = {
          debaterRole: data.debater_role || data.debater_id,
          debaterName: data.debater_name,
          roundNumber: data.round_number,
          position: data.position || data.perspective,
          argument: data.argument,
          evidenceCited: data.evidence_cited || data.challenges || [],
          counterPoints: data.counter_points || data.challenges || [],
          confidence: data.confidence || 0,
          timestamp: Date.now(),
        };
        setState((prev) => ({
          ...prev,
          debateArguments: [...prev.debateArguments, argument],
          debateRound: Math.max(prev.debateRound, data.round_number),
        }));
        break;
      }

      case 'debate_round_complete':
        setState((prev) => ({ ...prev, debateRound: data.round_number }));
        break;

      case 'debate_complete':
        // Debate complete
        break;

      case 'mediator_start':
        setState((prev) => ({ ...prev, phase: 'mediation' }));
        break;

      case 'mediator_consensus': {
        const consensus: MediatorConsensus = {
          consensusReached: data.consensus_reached,
          winningPosition: data.winning_position,
          confidence: data.confidence || 0,
          keyAgreements: data.key_agreements || [],
          unresolvedTensions: data.unresolved_tensions || [],
          finalRecommendation: data.final_recommendation,
          rationale: data.rationale,
        };
        setState((prev) => ({ ...prev, mediatorConsensus: consensus }));
        break;
      }

      case 'mediator_complete':
        // Mediator complete
        break;

      case 'aggregator_start':
        setState((prev) => ({ ...prev, phase: 'aggregation' }));
        break;

      case 'ghost_deck_slide': {
        // Handle individual slide streaming for real-time updates
        const newSlide: GhostDeckSlide = {
          slideNumber: data.slide_number,
          title: data.slide_title,
          headlineInsight: data.key_message,
          keyPoints: data.supporting_points || [],
          supportingEvidence: data.data_exhibits || [],
          slideType: 'analysis',
        };
        setState((prev) => {
          // Add slide if not already present (avoid duplicates)
          const existingSlideIndex = prev.ghostDeck.findIndex(
            (s) => s.slideNumber === newSlide.slideNumber
          );
          if (existingSlideIndex >= 0) {
            // Replace existing slide
            const updatedDeck = [...prev.ghostDeck];
            updatedDeck[existingSlideIndex] = newSlide;
            return { ...prev, ghostDeck: updatedDeck };
          }
          return { ...prev, ghostDeck: [...prev.ghostDeck, newSlide] };
        });
        break;
      }

      case 'ghost_deck_generated': {
        const slides: GhostDeckSlide[] = (data.slides || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (s: any) => ({
            slideNumber: s.slide_number,
            title: s.title,
            headlineInsight: s.headline_insight,
            keyPoints: s.key_points || [],
            supportingEvidence: s.supporting_evidence || [],
            workstreamSource: s.workstream_source,
            slideType: s.slide_type || 'analysis',
          })
        );
        setState((prev) => ({ ...prev, ghostDeck: slides }));
        break;
      }

      case 'executive_summary_ready':
        setState((prev) => ({
          ...prev,
          deliverable: {
            ...prev.deliverable,
            executiveSummary: data.summary,
            killSheet: {
              ...prev.deliverable?.killSheet,
              executiveSummary: data.summary,
              recommendation: data.recommendation,
              confidenceLevel: data.confidence_level,
            } as StrategicKillSheet,
          },
        }));
        break;

      case 'kill_sheet_ready':
        setState((prev) => ({
          ...prev,
          deliverable: {
            ...prev.deliverable,
            killSheet: {
              executiveSummary: data.kill_sheet?.executive_summary || '',
              recommendation: data.kill_sheet?.recommendation || '',
              confidenceLevel: data.kill_sheet?.confidence_level || '',
              keyFindings: data.kill_sheet?.key_findings || [],
              risks: data.kill_sheet?.risks || [],
              opportunities: data.kill_sheet?.opportunities || [],
              nextSteps: data.kill_sheet?.next_steps || [],
            },
          },
        }));
        break;

      case 'aggregator_complete':
        // Aggregator complete
        break;

      case 'done': {
        setState((prev) => {
          const deliverable: StrategicDeliverable = {
            killSheet: prev.deliverable?.killSheet,
            ghostDeck: prev.ghostDeck,
            executiveSummary: data.deliverable?.executive_summary,
            totalLatencySeconds: data.total_latency_seconds,
          };
          // Call onComplete after state update
          setTimeout(() => onCompleteRef.current?.(deliverable), 0);
          return {
            ...prev,
            phase: 'concluded',
            completedAt: data.completed_at,
            totalCreditsUsed: typeof data.total_cost === 'number' ? data.total_cost : undefined,
            deliverable: {
              ...prev.deliverable,
              ...deliverable,
            },
          };
        });
        disconnect();
        break;
      }

      case 'error': {
        const errorMsg = data.error || 'Unknown error';
        setState((prev) => ({
          ...prev,
          phase: 'error',
          error: errorMsg,
        }));
        onErrorRef.current?.(errorMsg);
        disconnect();
        break;
      }
    }
  } catch (e) {
    console.error(`Failed to parse ${eventType} event:`, e);
  }
}

